# Apply with SEEK brand guidelines

The [`applyWithSeekButton` query] automatically provides a button image to be directly embedded into your page.
The button image is returned with branding and translated copy appropriate to the supplied position and locale information.
SEEK recommends using button images provided by the query as it enables your software to receive branding changes without additional effort.

If you have custom button requirements, this document provides guidelines on composing an Apply with SEEK button.
Your software must either use the returned image from the SEEK API or a custom implementation that follows these guidelines to complete certification.

If you download and host these assets locally, they will become outdated when SEEK’s branding changes.
To keep your branding up to date, check for updates regularly.
Alternatively, use the [`applyWithSeekButton` query] to automatically keep your button in sync.

## Button assets

SEEK provides a bundle of Apply with SEEK button assets, including pre-approved buttons, text, and logo assets.
You can always find the latest version below.

import { Actions, ButtonLink, IconDownload, Text } from 'braid-design-system';

<Actions>
  <ButtonLink
    download
    href="https://integration.seek.com/assets/awsk-button-assets.zip"
    icon={<IconDownload />}
    tone="formAccent"
    variant="soft"
  >
    Button assets
  </ButtonLink>
</Actions>

<Text size="small">Last updated: 2025-03-14</Text>

[`applyWithSeekButton` query]: /use-cases/apply-with-seek/display-button

## Custom buttons

Using a button with SEEK branding is strongly recommended,
as it helps SEEK candidates to easily recognise and trust the feature,
increasing engagement.

If you need to adapt our button to match your visual design,
follow the guidelines below to ensure consistency with SEEK’s branding.

### Text

Populate your button with text provided from the [`applyWithSeekButton` query],
which includes the correct brand (SEEK, Jobstreet, Jobsdb) and translated copy.
This text must not be hardcoded.
It must be distinct and clearly visible on the button,
ensuring adequate spacing for legibility.

### Size

Resize the button as needed for different devices and screen sizes,
but preserve the aspect ratio so that the SEEK logo is not stretched.
Avoid cropping the logo or text.

|                                                                                 Standard                                                                                 |                                                                              Small                                                                              |
| :----------------------------------------------------------------------------------------------------------------------------------------------------------------------: | :-------------------------------------------------------------------------------------------------------------------------------------------------------------: |
| ![Layout guidelines for standard Apply with SEEK buttons](./assets/standard_button_layout.svg 'Layout guidelines for standard Apply with SEEK buttons =style=lightMode') | ![Layout guidelines for small Apply with SEEK buttons](./assets/small_button_layout.svg 'Layout guidelines for small Apply with SEEK buttons =style=lightMode') |

### Colour

Only use the colour combinations that are shown below.
The button must always include the appropriate colour variant of the SEEK logo.

| Theme        |                                                                                                             Example                                                                                                              | Description                                                                     |
| :----------- | :------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------: | :------------------------------------------------------------------------------ |
| Brand accent | ![Standard Brand Accent themed Apply with SEEK button](https://integration.seek.com/assets/awsk-button-assets/button-images/seek/en/standard-accent.svg 'Standard Brand Accent themed Apply with SEEK button. =style=lightMode') | <p>Fill: `#E60278`</p><p>Font: `#FFFFFF`</p>                                    |
| Light        |        ![Standard Light themed Apply with SEEK button](https://integration.seek.com/assets/awsk-button-assets/button-images/seek/en/standard-light.svg 'Standard Light themed Apply with SEEK button. =style=lightMode')         | <p>Fill: `#FFFFFF`</p><p>Stroke: `#2E3849` 2px inside</p><p>Font: `#2E3849`</p> |
| Dark         |          ![Standard Dark themed Apply with SEEK button](https://integration.seek.com/assets/awsk-button-assets/button-images/seek/en/standard-dark.svg 'Standard Dark themed Apply with SEEK button. =style=lightMode')          | <p>Fill: `#2E3849`</p><p>Font: `#FFFFFF`</p>                                    |

### Font

Ensure a sans-serif font is selected which is clearly legible and sized appropriately for the button.

Alternatively, SVG files of translated text in the SEEK Sans Medium font are included in the [bundle](#button-assets).

### Accessibility

If you use an image for the full button or button text,
add the [`buttonLabel`] returned from the [`applyWithSeekButton` query] as an accessible [`aria-label`] or [`alt`] on the button element.
Some examples are provided in our [Render the button] documentation.

[`alt`]: https://developer.mozilla.org/en-US/docs/Web/API/HTMLImageElement/alt#usage_notes
[`aria-label`]: https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Reference/Roles/button_role#basic_buttons
[`buttonLabel`]: /schema/named-type/ApplyWithSeekButtonPayload#/field/buttonLabel
[Render the button]: /use-cases/apply-with-seek/display-button#render-the-button

## Best practices

import { BestPracticesTable } from './best-practices-table';

<BestPracticesTable />

## Pre-approved buttons

The [bundle](#button-assets) also contains pre-approved buttons in SVG format across two aspect ratios.

Three button styles are included, along with variants for brands (SEEK, Jobstreet, Jobsdb) and languages across our Asia-Pacific markets.

### Button variants

import { ButtonModeTable } from './button-mode-table';

<ButtonModeTable />
